import { useEffect, useRef, useState } from 'react';
import { Controller, type DefaultValues, type SubmitHandler, type UseFormSetError, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z, ZodTypeAny } from 'zod';
import { createUserSchema } from '../schemas/user.schema';
import { Form, Input, Button, Label } from '@/shared/components';
import { Checkbox } from '@/shared/components/form';
import { DealerSelect } from '@/features/dealers/components/DealerSelect';
import type { Dealer } from '@/features/dealers/types/dealers.types';
import { UserSecurityGroupField } from './UserSecurityGroupField';
import { UserRoleField } from './UserRoleField';
import type { AssignUserToGroupsPayload } from '@/features/security-admin/types/security-admin.types';

interface UserFormProps<TSchema extends ZodTypeAny = typeof createUserSchema> {
  defaultValues?: DefaultValues<z.infer<TSchema>>;
  onSubmit: SubmitHandler<z.infer<TSchema>>;
  isLoading?: boolean;
  submitLabel?: string;
  schema?: TSchema;
  hideExtraFields?: boolean;
  /**
   * Invoked once on mount with the form's setError so the parent page can push
   * server-side 422 field errors next to the relevant inputs.
   */
  onSetErrorRef?: (setError: UseFormSetError<z.infer<TSchema>>) => void;
  /**
   * Invoked once on mount with a getter for the current optional security
   * group selection, read by the parent page at submit time.
   */
  onSecurityGroupSelectionRef?: (getSelection: () => AssignUserToGroupsPayload | null) => void;
  /**
   * Invoked once on mount with a getter for the current optional role
   * selection (role ids), read by the parent page at submit time.
   */
  onRoleSelectionRef?: (getSelection: () => string[]) => void;
}

export function UserForm<TSchema extends ZodTypeAny = typeof createUserSchema>({
  defaultValues,
  onSubmit,
  isLoading = false,
  submitLabel = 'Save',
  schema,
  hideExtraFields = false,
  onSetErrorRef,
  onSecurityGroupSelectionRef,
  onRoleSelectionRef,
}: UserFormProps<TSchema>) {
  const activeSchema = (schema ?? createUserSchema) as TSchema;
  const [selectedDealer, setSelectedDealer] = useState<Dealer | null>(null);
  const groupSelectionRef = useRef<AssignUserToGroupsPayload | null>(null);
  const roleSelectionRef = useRef<string[]>([]);
  const {
    handleSubmit,
    control,
    setError,
    setValue,
    // formState: { errors },
  } = useForm<z.infer<TSchema>>({
    mode: 'onBlur',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(activeSchema as any),
    defaultValues: {
      name: '',
      email: '',
      mfa_enforced: false,
      dealer_id: null,
      organisation_id: null,
      ...(defaultValues ?? {}),
    } as DefaultValues<z.infer<TSchema>>,
  });

  useEffect(() => {
    onSetErrorRef?.(setError);
  }, [onSetErrorRef, setError]);

  useEffect(() => {
    onSecurityGroupSelectionRef?.(() => groupSelectionRef.current);
  }, [onSecurityGroupSelectionRef]);

  useEffect(() => {
    onRoleSelectionRef?.(() => roleSelectionRef.current);
  }, [onRoleSelectionRef]);

  return (
    <Form className="space-y-4 max-w-lg" onSubmit={handleSubmit(onSubmit)}>
      <div>
        <Label htmlFor="name">Name</Label>
        <Controller
          name={"name" as Parameters<typeof control.register>[0]}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              id="name"
              type="text"
              placeholder="Full name"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={!!error}
              hint={error?.message}
              hintId="name-error"
              aria-invalid={!!error}
              aria-describedby={error ? 'name-error' : undefined}
            />
          )}
        />
      </div>

      <div>
        <Label htmlFor="email">Email</Label>
        <Controller
          name={"email" as Parameters<typeof control.register>[0]}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              id="email"
              type="email"
              placeholder="user@example.com"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={!!error}
              hint={error?.message}
              hintId="email-error"
              aria-invalid={!!error}
              aria-describedby={error ? 'email-error' : undefined}
            />
          )}
        />
      </div>

      {!hideExtraFields && (
        <>
          <div>
            <Controller
              name={"mfa_enforced" as Parameters<typeof control.register>[0]}
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="mfa_enforced"
                  checked={field.value}
                  onChange={field.onChange}
                  label="Enforce MFA"
                  labelClassName="text-sm"
                  ariaLabel="Enforce MFA"
                />
              )}
            />
          </div>

          <div>
            <Label htmlFor="dealer_id">Home Dealer</Label>
            <Controller
              name={"dealer_id" as Parameters<typeof control.register>[0]}
              control={control}
              render={({ field }) => (
                <DealerSelect
                  selected={selectedDealer}
                  onSelect={(dealer) => {
                    setSelectedDealer(dealer);
                    field.onChange(dealer?.id ?? null);
                    groupSelectionRef.current = null;
                    if (dealer) {
                      setValue(
                        'organisation_id' as Parameters<typeof control.register>[0],
                        null as never,
                      );
                    }
                  }}
                  placeholder="Search dealers by name, code, city, or postcode..."
                />
              )}
            />
          </div>

          <div>
            <Label htmlFor="organisation_id">Organisation ID <span className="text-mp-muted">(optional UUID)</span></Label>
            <Controller
              name={"organisation_id" as Parameters<typeof control.register>[0]}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Input
                  id="organisation_id"
                  type="text"
                  placeholder="Leave blank if not applicable"
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                  onBlur={field.onBlur}
                  error={!!error}
                  hint={error?.message}
                  hintId="organisation_id-error"
                  aria-invalid={!!error}
                  aria-describedby={error ? 'organisation_id-error' : undefined}
                />
              )}
            />
          </div>

          <UserRoleField
            onSelectionChange={(ids) => {
              roleSelectionRef.current = ids;
            }}
          />

          <UserSecurityGroupField
            key={selectedDealer?.id ?? 'no-dealer'}
            dealerId={selectedDealer?.id ?? null}
            onSelectionChange={(selection) => {
              groupSelectionRef.current = selection;
            }}
          />
        </>
      )}

      <Button size="sm" disabled={isLoading}>
        {isLoading ? 'Saving...' : submitLabel}
      </Button>
    </Form>
  );
}
